/**
 * Upload Storage option A (client + JWT + RLS) pour pièces jointes transaction.
 */

import { supabase } from '../supabaseClient';

export const TRANSACTION_ATTACHMENTS_BUCKET = 'transaction-attachments';

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/** Certains OS envoient image/jpg ou un type vide : le bucket refuse un Content-Type hors liste. */
function resolveImageContentType(file: File, safeExt: string): string {
  const t = file.type?.trim().toLowerCase();
  if (t === 'image/jpg') {
    return 'image/jpeg';
  }
  if (t && ALLOWED_TYPES.includes(t)) {
    return t;
  }
  const byExt: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
  };
  return byExt[safeExt] || 'image/jpeg';
}

function isAllowedImageFile(file: File, safeExt: string): boolean {
  const t = file.type?.trim().toLowerCase();
  if (t === 'image/jpg' || ALLOWED_TYPES.includes(t)) {
    return true;
  }
  return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(safeExt);
}

export async function uploadTransactionAttachment(
  transactionId: string,
  file: File,
  kind: 'transactions' | 'periodiques' | 'comptes' = 'transactions'
): Promise<{ path: string | null; error: string | null }> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return { path: null, error: 'Non connecté' };
  }
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const safeExt = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext) ? ext : 'jpg';
  if (!isAllowedImageFile(file, safeExt)) {
    return { path: null, error: 'Format non autorisé (JPEG, PNG, WebP, GIF)' };
  }
  if (file.size > MAX_BYTES) {
    return { path: null, error: 'Fichier trop volumineux (max 5 Mo)' };
  }
  const contentType = resolveImageContentType(file, safeExt);
  const path = `${user.id}/${kind}/${transactionId}/${Date.now()}.${safeExt}`;
  const { error } = await supabase.storage
    .from(TRANSACTION_ATTACHMENTS_BUCKET)
    .upload(path, file, { upsert: true, contentType });
  if (error) {
    return { path: null, error: error.message };
  }
  return { path, error: null };
}

export async function removeTransactionAttachmentFile(
  path: string
): Promise<{ error: string | null }> {
  const { error } = await supabase.storage.from(TRANSACTION_ATTACHMENTS_BUCKET).remove([path]);
  return { error: error?.message ?? null };
}

export async function getTransactionAttachmentSignedUrl(
  path: string,
  expiresIn = 3600
): Promise<{ url: string | null; error: string | null }> {
  const { data, error } = await supabase.storage
    .from(TRANSACTION_ATTACHMENTS_BUCKET)
    .createSignedUrl(path, expiresIn);
  if (error) {
    return { url: null, error: error.message };
  }
  return { url: data.signedUrl, error: null };
}
